import type { ClientDocumentCategory, ClientDocumentFile, ClientDocumentsDto } from '@fos/shared/contracts/knowledge';
import { Archive, Download, FileArchive, FileImage, FileSpreadsheet, FileText, Upload } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { errorMessage } from '../../../../lib/api';
import { cn } from '../../../../lib/cn';
import { formatDateShort } from '../../../../lib/format';
import { trpc } from '../../../../lib/trpc';
import { Button, IconButton } from '../../../../ui/Button';
import { CountPill } from '../../../../ui/CountPill';
import { Dialog } from '../../../../ui/Dialog';
import { ProjectChip } from '../../../../ui/EntityLabel';
import { FileDropzone, type RejectedFile, splitFiles } from '../../../../ui/FileDropzone';
import { SectionHeader } from '../../../../ui/PageHeader';
import { Section } from '../../../../ui/Section';
import { EmptyState } from '../../../../ui/States';
import { StatusPill } from '../../../../ui/StatusPill';
import { toast } from '../../../../ui/Toast';
import { TOUCH_BUTTON } from '../../../mobile';
import { CATEGORY_FILTER_LABELS, categoryLabel, fileKind, formatFileSize } from './documents-format';
import { UploadDocumentsDialog } from './UploadDocumentsDialog';

const KIND_ICON = { image: FileImage, sheet: FileSpreadsheet, archive: FileArchive, text: FileText } as const;

function reject(rejected: RejectedFile[]) {
  const first = rejected[0];
  if (!first) return;
  toast.error(rejected.length === 1 ? `Nie można dodać „${first.file.name}”` : 'Nie można dodać części plików', {
    description: first.message,
  });
}

/** Wiersz pliku: ikona typu, nazwa (pobranie), rodzaj, projekt, typ · rozmiar · data · autor, opis; pobierz i archiwum. */
function DocumentRow({ file, onArchive }: { file: ClientDocumentFile; onArchive?: () => void }) {
  const Icon = KIND_ICON[fileKind(file.filename)];
  const meta = [file.typeLabel, formatFileSize(file.sizeBytes), formatDateShort(file.createdAt), file.uploadedBy?.name]
    .filter(Boolean)
    .join(' · ');
  return (
    <li className="flex items-start gap-3 border-b border-line py-3">
      <span
        aria-hidden
        className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-surface text-fg-secondary"
      >
        <Icon size={16} strokeWidth={1.75} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <a
          href={file.downloadUrl}
          download={file.filename}
          className="min-w-0 rounded-xs text-body-s font-medium break-words text-fg hover:underline focus-visible:outline-2 focus-visible:outline-accent"
        >
          {file.filename}
        </a>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <StatusPill size="s" tone="neutral">
            {categoryLabel(file.category)}
          </StatusPill>
          {file.project && <ProjectChip name={file.project.name} />}
          <span className="text-caption text-fg-muted">{meta}</span>
        </div>
        {file.description && <p className="text-caption break-words text-fg-secondary">{file.description}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <a
          href={file.downloadUrl}
          download={file.filename}
          aria-label={`Pobierz ${file.filename}`}
          className="inline-flex size-8 items-center justify-center rounded-md text-fg-secondary hover:bg-elevated hover:text-fg focus-visible:outline-2 focus-visible:outline-accent max-md:size-11"
        >
          <Download size={16} strokeWidth={1.75} aria-hidden />
        </a>
        {onArchive && (
          <IconButton
            icon={Archive}
            label={`Przenieś do archiwum ${file.filename}`}
            size={32}
            iconSize={16}
            onClick={onArchive}
            className="max-md:size-11"
          />
        )}
      </div>
    </li>
  );
}

/**
 * „Pliki” w dokumentach klienta: przeciągnij i upuść / „Dodaj plik” (na telefonie też aparat), rodzaj, projekt, opis,
 * lista z rodzajem, datą i autorem, pobranie i archiwum (bez kasowania), filtr rodzaju.
 */
export function DocumentFilesSection({
  clientId,
  files,
  archived,
}: {
  clientId: string;
  files: ClientDocumentsDto['files'];
  archived: boolean;
}) {
  const utils = trpc.useUtils();
  const pickerRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState<ClientDocumentCategory | 'all'>('all');
  const [picked, setPicked] = useState<{ key: number; files: File[] } | null>(null);
  const [toArchive, setToArchive] = useState<ClientDocumentFile | null>(null);
  const canUpload = !archived && (files.canUploadToClient || files.uploadProjects.length > 0);

  const refresh = () => utils.clients.documents.list.invalidate({ clientId });
  const archive = trpc.knowledge.archiveFile.useMutation({
    onSuccess: async () => {
      toast.success('Plik w archiwum', { description: 'Zniknął z listy. Nie jest usunięty na zawsze.' });
      await refresh();
    },
    onError: (err) => toast.error('Nie udało się przenieść pliku do archiwum', { description: errorMessage(err) }),
    onSettled: () => setToArchive(null),
  });

  const counts = useMemo(() => {
    const out = new Map<ClientDocumentCategory, number>();
    for (const f of files.items) {
      const c = f.category ?? 'other';
      out.set(c, (out.get(c) ?? 0) + 1);
    }
    return out;
  }, [files.items]);
  const present = (Object.keys(CATEGORY_FILTER_LABELS) as ClientDocumentCategory[]).filter((c) => counts.has(c));
  const active = filter !== 'all' && counts.has(filter) ? filter : 'all';
  const shown = active === 'all' ? files.items : files.items.filter((f) => (f.category ?? 'other') === active);

  const open = (list: File[]) => setPicked({ key: Date.now(), files: list });

  return (
    <Section aria-labelledby="client-documents-files">
      <SectionHeader
        title={
          <span id="client-documents-files" className="inline-flex items-center gap-2">
            Pliki
            {files.items.length > 0 && <CountPill count={files.items.length} />}
          </span>
        }
        action={
          canUpload && files.items.length > 0 ? (
            <>
              <Button
                variant="secondary"
                size="s"
                icon={Upload}
                onClick={() => pickerRef.current?.click()}
                className={TOUCH_BUTTON}
              >
                Dodaj plik
              </Button>
              <input
                ref={pickerRef}
                type="file"
                multiple
                className="sr-only"
                tabIndex={-1}
                aria-hidden
                onChange={(e) => {
                  const { accepted, rejected } = splitFiles(Array.from(e.target.files ?? []), 10);
                  e.target.value = '';
                  reject(rejected);
                  if (accepted.length > 0) open(accepted);
                }}
              />
            </>
          ) : undefined
        }
      />

      {present.length > 1 && (
        <fieldset className="m-0 mb-1 flex min-w-0 flex-wrap gap-1.5 border-0 p-0">
          <legend className="sr-only">Rodzaj pliku</legend>
          {(['all', ...present] as const).map((c) => {
            const on = active === c;
            return (
              <button
                key={c}
                type="button"
                aria-pressed={on}
                onClick={() => setFilter(c)}
                className={cn(
                  'inline-flex h-7 items-center gap-1.5 rounded-full px-3 text-caption font-medium transition-colors max-md:h-9',
                  'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
                  on ? 'bg-accent-subtle text-accent' : 'bg-soft text-fg-secondary hover:bg-soft-hover hover:text-fg',
                )}
              >
                {c === 'all' ? 'Wszystkie' : CATEGORY_FILTER_LABELS[c]}
                <span className="tabular opacity-70">{c === 'all' ? files.items.length : counts.get(c)}</span>
              </button>
            );
          })}
        </fieldset>
      )}

      {files.items.length === 0 ? (
        canUpload ? (
          <FileDropzone
            title="Wgraj umowę albo przeciągnij plik tutaj"
            onFiles={open}
            onReject={reject}
            aria-label="Wgraj plik klienta"
          />
        ) : (
          <EmptyState
            compact
            icon={FileText}
            title="Brak plików"
            description={archived ? 'Klient jest w archiwum.' : 'Pliki klienta pojawią się tutaj.'}
          />
        )
      ) : (
        <>
          <ul aria-label="Pliki klienta">
            {shown.map((f) => (
              <DocumentRow key={f.id} file={f} onArchive={f.canArchive ? () => setToArchive(f) : undefined} />
            ))}
          </ul>
          {canUpload && (
            <FileDropzone
              size="s"
              className="mt-4"
              onFiles={open}
              onReject={reject}
              hint="PDF, Word, Excel, obrazy, ZIP — do 25 MB"
              aria-label="Dodaj kolejne pliki"
            />
          )}
        </>
      )}

      {picked && (
        <UploadDocumentsDialog
          key={picked.key}
          open
          onOpenChange={(next) => !next && setPicked(null)}
          files={picked.files}
          clientId={clientId}
          categories={files.categories}
          canUploadToClient={files.canUploadToClient}
          uploadProjects={files.uploadProjects}
          onUploaded={() => void refresh()}
        />
      )}

      <Dialog
        open={toArchive !== null}
        onOpenChange={(next) => !next && setToArchive(null)}
        width="s"
        title="Przenieść plik do archiwum?"
        description={
          toArchive
            ? `„${toArchive.filename}” zniknie z listy i nie będzie można go pobrać. Plik zostaje w archiwum — nie jest usuwany.`
            : undefined
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setToArchive(null)} className={TOUCH_BUTTON}>
              Anuluj
            </Button>
            <Button
              variant="primary"
              loading={archive.isPending}
              onClick={() => toArchive && archive.mutate({ id: toArchive.id })}
              className={TOUCH_BUTTON}
            >
              Przenieś do archiwum
            </Button>
          </>
        }
      />
    </Section>
  );
}
